import React, { useEffect, useState } from "react";

import clsx from "clsx";

import { LoadingSlideNoText } from "@components/title-slide-view";
import { type Annotation } from "@features/custom-annotations";
import { PrimaryButton, TertiaryButton } from "@shared/buttons";
import { FormattedDateRange } from "@shared/date-range-picker";

import { useAnnotationParam } from "../hooks/use-annotation-param";

import { AnnotationFilterNotification } from "./annotation-filter-notification";
import {
  AnnotationSortSelect,
  INITIAL_SORT,
  type SortValue,
} from "./annotation-sort-select";
import { Annotations } from "./annotations";

interface Props {
  isMmm?: boolean;
  newAnnotation: () => void;
  editAnnotation: (annotation: Annotation) => void;
  annotations: Annotation[];
  isAnnotationsLoading: boolean;
  fetchAnnotations: (arg: {
    variables: { startDate: string; endDate: string };
  }) => void;
}

export const AnnotationList = ({
  isMmm = false,
  newAnnotation,
  editAnnotation,
  annotations,
  isAnnotationsLoading,
  fetchAnnotations,
}: Props) => {
  const { closeFlyout, annotationParam } = useAnnotationParam();
  const [currentSort, setCurrentSort] = useState<SortValue | null>(
    INITIAL_SORT,
  );

  useEffect(() => {
    if (annotationParam) {
      fetchAnnotations({ variables: annotationParam });
    }
  }, [annotationParam, fetchAnnotations]);

  const title = isMmm ? "Calendar of events" : "Notes";
  const annotationText = isMmm ? "calendar event" : "note";

  return (
    <div>
      <div className="flex items-center justify-between mt-6 mb-1">
        <h2 className="font-bold text-2xl">{title}</h2>
        <TertiaryButton
          className="p-0 w-8 h-8 flex items-center justify-center group/button"
          onClick={closeFlyout}
          data-testid="close-button"
        >
          <i
            className={clsx(
              "fa-regular fa-sharp fa-times text-2xl",
              "text-interstellar group-hover/button:text-cosmic",
            )}
          />
        </TertiaryButton>
      </div>
      <p className="text-gray+1 text-sm mb-5">
        Displaying {annotationText}s for{" "}
        {annotationParam && <FormattedDateRange dateRange={annotationParam} />}
      </p>
      <div className="flex justify-between items-end">
        <AnnotationSortSelect value={currentSort} onChange={setCurrentSort} />
        <PrimaryButton
          isDisabled={isAnnotationsLoading}
          onClick={newAnnotation}
          className="h-8 uppercase font-bold"
        >
          NEW {annotationText.toUpperCase()}
        </PrimaryButton>
      </div>
      <AnnotationFilterNotification />
      {isAnnotationsLoading ? (
        <div className="py-8">
          <LoadingSlideNoText />
        </div>
      ) : (
        <Annotations
          annotations={annotations}
          currentSort={currentSort}
          editAnnotation={editAnnotation}
        />
      )}
    </div>
  );
};
